import React, { useCallback } from 'react'

import cx from 'classnames'
import { isEmpty, isNil, pickBy } from 'ramda'

import { Button } from '@driftt/dds'

import { toKebabCase } from 'utils/ramda'
import { trackEvent } from 'utils/tracking'

interface IExportButtonProps {
  isExportPending?: boolean
  className?: string
  queryConfig?: any
  isDisabled?: boolean
  onClick?: (...args: any[]) => void
}

const isValidParam = (value: any) => !isNil(value) || isEmpty(value)

const ExportButton = ({
  className,
  isExportPending = false,
  isDisabled = false,
  queryConfig = {},
  onClick,
}: IExportButtonProps) => {
  const trackClick = useCallback(() => {
    const pathname = window.location.pathname

    // Only conversations reports support these properties
    // But even in other reports the whole queryConfig is passed, so we only create them
    // If we're downloading from a conversation-related report
    const isConversationReport =
      pathname.includes('conversation') || pathname.includes('team')
    const convoProperties = isConversationReport
      ? {
          conversation_type: queryConfig.botStatus,
          widget_status: isNil(queryConfig.widgetStatus)
            ? null
            : queryConfig.widgetStatus
            ? 'OFFLINE'
            : 'ONLINE',
          inbox_selected: !!queryConfig.inboxSelected,
          lead_stage_selected: !!queryConfig.classificationId,
        }
      : {}

    trackEvent(
      'CSV Download Clicked',
      pickBy(isValidParam, {
        page: pathname,
        csv_type: queryConfig.metric
          ? toKebabCase(queryConfig.metric)
          : 'unknown',
        start_date: queryConfig.startDate,
        end_date: queryConfig.endDate,
        team_selected: !!queryConfig.teamSelected,
        teammates_count: isValidParam(queryConfig.agentsSelected)
          ? queryConfig.agentsSelected.length
          : 0,
        ...convoProperties,
      })
    )
  }, [queryConfig])

  const onButtonClick = useCallback(
    (...args: any[]) => {
      if (onClick) {
        try {
          onClick(...args)
        } finally {
          trackClick()
        }
      }
    },
    [onClick, trackClick]
  )

  return (
    <Button
      className={cx('export-button', className)}
      disabled={isDisabled}
      variant="secondary"
      loading={isExportPending}
      onClick={onButtonClick}
    >
      {isExportPending ? 'Exporting…' : 'Export CSV'}
    </Button>
  )
}

export default ExportButton
